import type { KeycloakClientRole } from '../../auth/clientRoles';
import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import type { UpdateDirectoryUserInput } from '../../types/user';
import type { CurrentUserProfile } from '../../types/user';
import type { I18nContextValue } from '../../types/i18nContext';
import {
  pickUserOptimisticLockFields,
  type UserOptimisticLockFields,
} from '../../utils/userUpdateUtils';

type Translate = I18nContextValue['translate'];

type MutationLike<TVariables> = {
  mutate: (variables: TVariables, options?: object) => void;
};

type TenantSetRolesVariables = {
  userId: string;
  roleNames: readonly KeycloakClientRole[];
};

type TenantUploadPhotoVariables = {
  userId: string;
  file: File;
};

type TenantDeletePhotoVariables = {
  userId: string;
};

export function submitTenantUserDetailsForm(options: {
  translate: Translate;
  userId: string;
  profile: CurrentUserProfile;
  trimmedUsername: string;
  trimmedEmail: string;
  firstName: string;
  lastName: string;
  validFrom: string;
  validTo: string;
  role: KeycloakClientRole;
  loadedRole: KeycloakClientRole;
  isRoleLoadError: boolean;
  rolesData: string[] | undefined;
  photoChanged: boolean;
  pendingFile: File | null;
  removePhoto: boolean;
  updateUserMutation: MutationLike<UpdateDirectoryUserInput>;
  setUserRolesMutation: MutationLike<TenantSetRolesVariables>;
  uploadPhotoMutation: MutationLike<TenantUploadPhotoVariables>;
  deletePhotoMutation: MutationLike<TenantDeletePhotoVariables>;
  bumpPhotoReloadKey: () => void;
}): void {
  const {
    translate,
    userId,
    profile,
    trimmedUsername,
    trimmedEmail,
    firstName,
    lastName,
    validFrom,
    validTo,
    role,
    loadedRole,
    isRoleLoadError,
    rolesData,
    photoChanged,
    pendingFile,
    removePhoto,
    updateUserMutation,
    setUserRolesMutation,
    uploadPhotoMutation,
    deletePhotoMutation,
    bumpPhotoReloadKey,
  } = options;

  const persistPhotoChanges = () => {
    if (pendingFile) {
      uploadPhotoMutation.mutate(
        { userId, file: pendingFile },
        {
          onSuccess: () => {
            toast.success({
              title: translate(
                'organisationManagement.users.details.photoUploadSuccessTitle',
              ),
            });
            bumpPhotoReloadKey();
          },
          onError: (error: unknown) => {
            void toastMutationError(
              error,
              translate(
                'organisationManagement.users.details.photoUploadErrorTitle',
              ),
            );
          },
        },
      );
      return;
    }

    if (removePhoto) {
      deletePhotoMutation.mutate(
        { userId },
        {
          onSuccess: () => {
            toast.success({
              title: translate(
                'organisationManagement.users.details.photoRemoveSuccessTitle',
              ),
            });
            bumpPhotoReloadKey();
          },
          onError: (error: unknown) => {
            void toastMutationError(
              error,
              translate(
                'organisationManagement.users.details.photoRemoveErrorTitle',
              ),
            );
          },
        },
      );
    }
  };

  updateUserMutation.mutate(
    {
      userId,
      username: trimmedUsername,
      email: trimmedEmail,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      validFrom: validFrom.trim() || undefined,
      validTo: validTo.trim() || undefined,
      ...pickUserOptimisticLockFields(profile as UserOptimisticLockFields),
    },
    {
      onSuccess: () => {
        toast.success({
          title: translate(
            'organisationManagement.users.details.saveSuccessTitle',
          ),
          description: translate(
            'organisationManagement.users.details.saveSuccessDescription',
            { username: trimmedUsername },
          ),
        });

        const shouldUpdateRole =
          !isRoleLoadError && rolesData != null && role !== loadedRole;
        if (shouldUpdateRole) {
          setUserRolesMutation.mutate(
            {
              userId,
              roleNames: [role],
            },
            {
              onError: (error: unknown) => {
                void toastMutationError(
                  error,
                  translate(
                    'organisationManagement.users.details.roleErrorTitle',
                  ),
                  translate(
                    'organisationManagement.users.details.roleErrorDescription',
                  ),
                );
              },
            },
          );
        }

        if (photoChanged) {
          persistPhotoChanges();
        }
      },
      onError: (error: unknown) => {
        void toastMutationError(
          error,
          translate('organisationManagement.users.details.saveErrorTitle'),
          translate(
            'organisationManagement.users.details.saveErrorDescription',
          ),
        );
      },
    },
  );
}
